/**
 * Helper — Grandeurs & mesures 4ᵉ (attendus 178-179, O1-O2).
 *
 * Deux domaines :
 *   1. VOLUMES (O1, gen_178) : pyramide (base carrée / rectangle) et cône.
 *      V = (aire de la base × hauteur) ÷ 3. Tout par construction : les tirages
 *      garantissent un volume ENTIER (B·h ≡ 0 mod 3) ; le cône est rendu par son
 *      coefficient de π (r²·h/3 entier, π jamais tapé) ou par une valeur
 *      approchée arrondie à l'unité, servie seulement quand les arrondis avec
 *      3,14, avec la touche π et avec π arrondi d'abord au centième coïncident
 *      (coneArrondisConcordent). Figures en perspective cavalière (svgPyramide,
 *      svgCone), PARTAGÉES avec 1241 (3e) et 185 (espace) : ne pas en changer le
 *      dessin sans relancer le harnais sur ces gens.
 *   2. CONVERSIONS DE GRANDEURS COMPOSÉES (O2, gen_179) : vitesses km/h ↔ m/s
 *      et débits m³/s ↔ L/min, EXACTES (entiers mis à l'échelle, aucun arrondi).
 *
 * Revue 2026-09-23 (vague 3) : phrases fausses retirées de cet en-tête (aucun
 * solveur ne partage l'arrondi du cône ; les durées et les capacités n'étaient
 * pas des grandeurs composées) ; helpers des cartes sorties de 179 retirés
 * (durées, capacités ↔ volumes, débit calculé, table de vitesses plausibles,
 * conversions arrondies au dixième) ; `imgElement` prend un `alt` facultatif
 * (sans lui, sortie identique : 1241 inchangé) ; `svgPyramide` et `svgCone`
 * prennent une option `lettreMath` (le sommet S en KaTeX_Math italique ;
 * fausse par défaut : 1241 et 185 inchangés) ; `coneArrondisConcordent`
 * ajouté.
 */

import { randInt, randomChoice, randomChoiceMultiple } from '../../../utils';

// ═══════════════════════════════════════════════════════════
// Formatage décimal EXACT (entier mis à l'échelle → chaîne FR)
// ═══════════════════════════════════════════════════════════

/**
 * Valeur = v × 10^(−dec) → chaîne FR. Ex : (3744, 2) → "37,44" ; (720, 1) → "72".
 * Zéros de fin retirés (pas d'ambiguïté « 8,10 »).
 */
export const frFromScaled = (v, dec) => {
    const neg = v < 0; const a = Math.abs(v);
    const p = 10 ** dec;
    const ent = Math.trunc(a / p);
    const frac = a % p;
    let s = String(ent);
    if (frac > 0) {
        const f = String(frac).padStart(dec, '0').replace(/0+$/, '');
        if (f) s += `,${f}`;
    }
    return (neg ? '-' : '') + s;
};

// ═══════════════════════════════════════════════════════════
// VOLUMES — pyramide & cône (O1, gen_178)
// ═══════════════════════════════════════════════════════════

/**
 * Tire une pyramide à base CARRÉE (côté a, hauteur h) de volume ENTIER.
 * a ∈ 3..12, h choisi pour que a²·h ≡ 0 mod 3 (si a non multiple de 3, h l'est).
 */
export const tirerPyramideCarree = () => {
    const a = randInt(3, 12);
    // a² mod 3 : si a ≡ 0 [3] alors a² ≡ 0, sinon a² ≡ 1 → h doit être multiple de 3.
    const h = (a % 3 === 0) ? randInt(4, 15) : 3 * randInt(2, 5);
    const V = (a * a * h) / 3;                 // entier par construction
    return { a, h, V };
};

/**
 * Pyramide à base RECTANGLE (L, l, h) de volume ENTIER (L·l·h ≡ 0 mod 3).
 */
export const tirerPyramideRect = () => {
    const L = randInt(5, 14);
    const l = randInt(3, L - 1);
    const base = L * l;
    const h = (base % 3 === 0) ? randInt(4, 12) : 3 * randInt(2, 5);
    const V = (base * h) / 3;
    return { L, l, h, V };
};

/**
 * Cône (rayon r, hauteur h) : coefficient de π = r²·h/3 ENTIER.
 * r choisi multiple de 3 OU h multiple de 3 pour l'entier.
 */
export const tirerCone = () => {
    const r = randInt(2, 9);
    const h = (r % 3 === 0) ? randInt(4, 12) : 3 * randInt(2, 5);
    const coef = (r * r * h) / 3;              // V = coef × π
    return { r, h, coef };
};

/** Valeur approchée du volume du cône avec π ≈ 3,14, arrondie à l'unité (cm³) : coef × 314 est un entier de centièmes. */
export const coneApprox = (coef) => Math.round((coef * 314) / 100);

/**
 * Vrai si le volume du cône arrondi à l'unité est le MÊME quelle que soit la
 * façon de faire de l'élève : π ≈ 3,14, π ≈ 3,1416, la touche π (le cours :
 * « 18π ≈ 56,55 »), ou la touche π avec un arrondi au centième d'abord puis à
 * l'unité. Sur les 47 couples (r ; h) du tirage, 38 passent (60π = 188,4955…
 * s'écrit 188,50 au centième et s'arrondirait à 189 : écarté).
 */
export const coneArrondisConcordent = (coef) => {
    const a314 = coneApprox(coef);
    const aPi = Math.round(coef * Math.PI);
    const aPiCentieme = Math.round(Math.round(coef * Math.PI * 100) / 100);
    const a31416 = Math.round(coef * 3.1416);
    return a314 === aPi && aPi === aPiCentieme && aPi === a31416;
};

// ═══════════════════════════════════════════════════════════
// Perspective cavalière — pyramide & cône
// ═══════════════════════════════════════════════════════════

const NOIR = '#1f2937';
const ROUGE = '#DC2626';
const R1 = (v) => Number(v).toFixed(1);

/**
 * Élément additionnel image (canal normatif §12 FORMATS_TANOED). `alt`
 * facultatif (178) : il décrit la figure, jamais la réponse ; sans lui, la
 * sortie est inchangée (1241).
 */
export const imgElement = (svg, maxWidth = 'small', alt = '') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
        ...(alt ? { alt } : {}),
    } }],
});

const svgWrap = (w, h, inner) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial">${inner}</svg>`;

const ligne = (P, Q, { dash = false, couleur = NOIR, ep = 1.8 } = {}) =>
    `<line x1="${R1(P.x)}" y1="${R1(P.y)}" x2="${R1(Q.x)}" y2="${R1(Q.y)}" stroke="${couleur}" stroke-width="${ep}" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}/>`;

/** Une lettre de point en KaTeX_Math italique (règle prof 12/09), dans un <text>. */
const lettreSvg = (l) => `<tspan font-family="KaTeX_Math, 'Times New Roman', serif" font-style="italic">${l}</tspan>`;

// Halo blanc (paint-order stroke) → l'étiquette reste lisible par-dessus une arête.
const texte = (P, txt, { dx = 0, dy = 0, fs = 13, couleur = '#b45309', anchor = 'middle' } = {}) =>
    `<text x="${R1(P.x + dx)}" y="${R1(P.y + dy)}" font-size="${fs}" text-anchor="${anchor}" fill="${couleur}" stroke="#ffffff" stroke-width="3" paint-order="stroke" stroke-linejoin="round">${txt}</text>`;

/**
 * Pyramide en perspective cavalière. Base ABCD (parallélogramme), sommet S au-
 * dessus du centre. Arêtes cachées (vers le sommet arrière-gauche D) en pointillés.
 * `base` = 'carre' | 'rectangle'. Étiquettes de dimensions optionnelles.
 */
/**
 * `triangle: true` (3e, 1241) : trace aussi le segment du pied de la hauteur O
 * au sommet avant B, en pointillés : le triangle rectangle SOB se voit.
 * `lettreMath: true` (178, revue 2026-09-23) : le nom du sommet S en KaTeX_Math
 * italique (règle prof 12/09) ; sans lui, dessin inchangé (1241, 185).
 */
export const svgPyramide = ({ base = 'carre', labels = {}, triangle = false, lettreMath = false } = {}) => {
    const w = 240; const h = 210;
    // Géométrie écran (indépendante des valeurs numériques : figure illustrative).
    const larg = base === 'rectangle' ? 130 : 96;
    const prof = 54; const alpha = 30 * Math.PI / 180;
    const ox = prof * Math.cos(alpha) * 0.6; const oy = prof * Math.sin(alpha) * 0.6;
    const x0 = 46; const yBase = 168;
    const A = { x: x0, y: yBase };
    const B = { x: x0 + larg, y: yBase };
    const C = { x: x0 + larg + ox, y: yBase - oy };
    const D = { x: x0 + ox, y: yBase - oy };
    const O = { x: (A.x + C.x) / 2, y: (A.y + C.y) / 2 };
    const S = { x: O.x, y: 24 };
    let s = '';
    // Base : arêtes vues (AB, BC) pleines ; cachées (CD, DA) pointillées.
    s += ligne(A, B);
    s += ligne(B, C);
    s += ligne(C, D, { dash: true });
    s += ligne(D, A, { dash: true });
    // Arêtes latérales : SA, SB, SC pleines ; SD cachée pointillée.
    s += ligne(S, A);
    s += ligne(S, B);
    s += ligne(S, C);
    s += ligne(S, D, { dash: true });
    // Hauteur (S → O) pointillée + petit carré d'angle droit.
    s += ligne(S, O, { dash: true, couleur: ROUGE, ep: 1.4 });
    s += `<rect x="${R1(O.x)}" y="${R1(O.y - 9)}" width="9" height="9" fill="none" stroke="${ROUGE}" stroke-width="1"/>`;
    // Le côté [OB] du triangle rectangle SOB (3e, sur demande).
    if (triangle) s += ligne(O, B, { dash: true, couleur: ROUGE, ep: 1.4 });
    // Sommet.
    s += texte(S, lettreMath ? lettreSvg('S') : 'S', { dy: -6, couleur: '#0f172a' });
    // Étiquettes de dimensions.
    if (labels.cote) s += texte({ x: (A.x + B.x) / 2, y: yBase }, labels.cote, { dy: 18 });
    if (labels.L) s += texte({ x: (A.x + B.x) / 2, y: yBase }, labels.L, { dy: 18 });
    if (labels.l) s += texte({ x: (B.x + C.x) / 2, y: (B.y + C.y) / 2 }, labels.l, { dx: 16, dy: 4 });
    if (labels.h) s += texte({ x: O.x, y: (S.y + O.y) / 2 }, labels.h, { dx: 12, anchor: 'start' });
    return svgWrap(w, h, s);
};

/**
 * Cône en perspective cavalière. Ellipse de base (demi-avant pleine, demi-arrière
 * pointillée), sommet S, deux génératrices, hauteur pointillée + angle droit.
 */
export const svgCone = ({ labels = {}, lettreMath = false } = {}) => {
    const w = 220; const h = 210;
    const cx = 110; const cyB = 158; const rx = 66; const ry = 20;
    const S = { x: cx, y: 26 };
    const G = { x: cx - rx, y: cyB }; const Dr = { x: cx + rx, y: cyB };
    let s = '';
    // Ellipse de base : arrière (haut) pointillé, avant (bas) plein.
    s += `<path d="M ${R1(cx - rx)},${R1(cyB)} A ${rx},${ry} 0 0 1 ${R1(cx + rx)},${R1(cyB)}" fill="none" stroke="${NOIR}" stroke-width="1.6" stroke-dasharray="5 4"/>`;
    s += `<path d="M ${R1(cx - rx)},${R1(cyB)} A ${rx},${ry} 0 0 0 ${R1(cx + rx)},${R1(cyB)}" fill="none" stroke="${NOIR}" stroke-width="1.8"/>`;
    // Génératrices.
    s += ligne(S, G); s += ligne(S, Dr);
    // Hauteur + rayon.
    s += ligne(S, { x: cx, y: cyB }, { dash: true, couleur: ROUGE, ep: 1.4 });
    s += `<rect x="${R1(cx)}" y="${R1(cyB - 9)}" width="9" height="9" fill="none" stroke="${ROUGE}" stroke-width="1"/>`;
    s += ligne({ x: cx, y: cyB }, Dr, { couleur: '#2563eb', ep: 1.3 });
    // Étiquettes.
    s += texte(S, lettreMath ? lettreSvg('S') : 'S', { dy: -6, couleur: '#0f172a' });
    if (labels.r) s += texte({ x: (cx + Dr.x) / 2, y: cyB }, labels.r, { dy: -6, couleur: '#2563eb' });
    if (labels.h) s += texte({ x: cx, y: (S.y + cyB) / 2 }, labels.h, { dx: 12, anchor: 'start' });
    return svgWrap(w, h, s);
};

// ═══════════════════════════════════════════════════════════
// CONVERSIONS DE GRANDEURS COMPOSÉES (O2, gen_179)
// ═══════════════════════════════════════════════════════════

// ── Vitesses km/h ↔ m/s (exactes) ─────────────────────────
/**
 * km/h → m/s EXACT au dixième : v km/h = v × 1 000 m / 3 600 s = v × 25 / 9
 * dixièmes de m/s, exact dès que v est un multiple de 9 (9 km/h = 2,5 m/s,
 * 18 km/h = 5 m/s). Throw sinon : aucune conversion arrondie n'est servie.
 */
export const kmhVersMsExact = (kmh) => {
    if ((kmh * 25) % 9 !== 0) throw new Error(`grandeurs : ${kmh} km/h ne se convertit pas exactement en m/s`);
    const dixiemes = (kmh * 25) / 9;
    return { dixiemes, txt: frFromScaled(dixiemes, 1) };
};

/** m/s (dixièmes) → km/h EXACT : × 3,6, soit msD × 36 centièmes de km/h. */
export const msVersKmhExact = (msD) => ({ centiemes: msD * 36, txt: frFromScaled(msD * 36, 2) });

// ── Débits m³/s ↔ L/min (exacts) ──────────────────────────
/**
 * Un débit en m³/s écrit mant × 10^(−dec) (dec ≤ 2) et son équivalent en
 * L/min : 1 m³/s = 1 000 L/s = 60 000 L/min, donc mant × 60 000 / 10^dec,
 * toujours entier (60 000 est divisible par 100).
 */
export const debitM3sLmin = (mant, dec) => {
    const lmin = (mant * 60000) / (10 ** dec);
    if (!Number.isInteger(lmin)) throw new Error(`grandeurs : débit ${mant}e-${dec} m³/s non entier en L/min`);
    return { m3s: frFromScaled(mant, dec), lmin: String(lmin), mant, dec };
};

export { randInt, randomChoice, randomChoiceMultiple };
